import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Input,
    Tabs,
    TabsHeader,
    TabsBody,
    Tab,
    TabPanel,
    Alert,
    Select,
    Option
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDollarSign,
    faCheckCircle,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import ChargeApplication from './ChargeApplication';
import RemittanceSummary from './RemittanceSummary';
const { rootUrl } = require('../../../config/config');

const WeeklyRemittanceProcessor = () => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [weekKey, setWeekKey] = useState('');
    const [plateNo, setPlateNo] = useState('');
    const [cars, setCars] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [processing, setProcessing] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('process');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        fetchCars();
        fetchWeeks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCars = async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/cars`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );
            setCars(response.data || []);
        } catch (error) {
            console.error('Error fetching cars:', error);
        }
    };

    const fetchWeeks = async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/system/week`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );
            if (response.data && response.data.success) {
                setWeeks(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching weeks:', error);
        }
    };

    const handleProcess = async () => {
        setError(null);
        setSuccess(null);

        if (!weekKey || !plateNo) {
            setError('Please select week and plate number');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(
                `${rootUrl}/api/remittance/process`,
                {
                    week_key: weekKey,
                    plate_no: plateNo,
                    processed_by: localStorage.getItem('username') || 'user',
                    notes: ''
                },
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                setProcessing(response.data.data);
                setSuccess(`Remittance for ${plateNo} created successfully!`);
                setTimeout(() => {
                    setActiveTab('charges');
                    setSuccess(null);
                }, 2000);
            } else {
                setError(response.data?.message || 'Failed to process remittance');
            }
        } catch (error) {
            console.error('Error processing remittance:', error);
            setError(error.response?.data?.message || 'Failed to process remittance');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0.00';
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount);
    };

    return (
        <div className="space-y-4">
            {error && (
                <Alert
                    color="red"
                    icon={<FontAwesomeIcon icon={faExclamationTriangle} />}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {success && (
                <Alert
                    color="green"
                    icon={<FontAwesomeIcon icon={faCheckCircle} />}
                    onClose={() => setSuccess(null)}
                >
                    {success}
                </Alert>
            )}

            <Typography variant="h4" className="text-gray-800">
                Weekly Remittance Processor
            </Typography>

            <Tabs value={activeTab} onChange={setActiveTab}>
                <TabsHeader>
                    <Tab value="process">Process Remittance</Tab>
                    <Tab value="charges">Apply Charges</Tab>
                    <Tab value="summary">Summary</Tab>
                </TabsHeader>
                <TabsBody>
                    <TabPanel value="process">
                        <Card>
                            <CardBody className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Select
                                        label="Week"
                                        value={weekKey}
                                        onChange={(val) => setWeekKey(val)}
                                    >
                                        {weeks.map((week) => (
                                            <Option key={week.week} value={week.week}>
                                                {week.week} ({new Date(week.start_date).toLocaleDateString()} - {new Date(week.end_date).toLocaleDateString()})
                                            </Option>
                                        ))}
                                    </Select>

                                    <Select
                                        label="Plate Number"
                                        value={plateNo}
                                        onChange={(val) => setPlateNo(val)}
                                    >
                                        {cars.map((car) => (
                                            <Option key={car.plate_no} value={car.plate_no}>
                                                {car.plate_no} - {car.make} {car.model}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>

                                <Button
                                    color="green"
                                    onClick={handleProcess}
                                    disabled={loading || !weekKey || !plateNo}
                                    className="w-full"
                                >
                                    <FontAwesomeIcon icon={faDollarSign} className="mr-2" />
                                    {loading ? 'Processing...' : 'Process Remittance'}
                                </Button>

                                {processing && (
                                    <Card className="bg-green-50">
                                        <CardBody>
                                            <Typography variant="h6" className="text-green-800 mb-2">
                                                Remittance Processing Created
                                            </Typography>
                                            <div className="space-y-2">
                                                <Typography variant="small">
                                                    Total Remittance: {formatCurrency(processing.total_remittance)}
                                                </Typography>
                                                <Typography variant="small">
                                                    Total Charges: {formatCurrency(processing.total_charges)}
                                                </Typography>
                                                <Typography variant="small">
                                                    Owner Payout: {formatCurrency(processing.owner_payout)}
                                                </Typography>
                                            </div>
                                        </CardBody>
                                    </Card>
                                )}
                            </CardBody>
                        </Card>
                    </TabPanel>

                    <TabPanel value="charges">
                        {processing ? (
                            <ChargeApplication
                                remittanceProcessingId={processing.id}
                                plateNo={plateNo}
                                weekKey={weekKey}
                                onUpdate={(updated) => setProcessing(updated)}
                            />
                        ) : (
                            <Card>
                                <CardBody className="text-center py-8">
                                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="text-gray-400 mb-2" />
                                    <Typography variant="paragraph" className="text-gray-600">
                                        Please process a remittance first
                                    </Typography>
                                </CardBody>
                            </Card>
                        )}
                    </TabPanel>

                    <TabPanel value="summary">
                        {weekKey ? (
                            <RemittanceSummary weekKey={weekKey} plateNo={plateNo} />
                        ) : (
                            <Card>
                                <CardBody className="text-center py-8">
                                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="text-gray-400 mb-2" />
                                    <Typography variant="paragraph" className="text-gray-600">
                                        Please select a week to view summary
                                    </Typography>
                                </CardBody>
                            </Card>
                        )}
                    </TabPanel>
                </TabsBody>
            </Tabs>
        </div>
    );
};

export default WeeklyRemittanceProcessor;

